import React, { Fragment, useContext, useEffect, useState } from "react";
import {
  Container,
  Button,
  Card,
  Form,
  InputGroup,
  Alert,
} from "react-bootstrap";
import "../../App.css";
import { HiOutlineArrowNarrowLeft } from "react-icons/hi";
import { PiCoinBold } from "react-icons/pi";
import { BsSearch, BsShieldCheck, BsInfoCircleFill } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { usersData } from "../../App";
import { Common_Event_Hit } from "../../Actions/UserEvents";
import ErrorModal from "../Modals/ErrorModal";
import RedeemCoinModal from "../Modals/RedeemCoinModal";
import {
  sendMobileOtp,
  validateOtp,
  searchPatient,
  getAllPoints,
  getRetailerLedgerBalance,
} from "../../Actions/CureCoin";

const CureCoin = () => {
  const [coinBalance, setCoinBalance] = useState(0);
  const [errorModal, setErrorModal] = useState({ show: false, content: "" });
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const { userData } = useContext(usersData);
  const navigate = useNavigate();

  const [myCureCoinBalance, setMyCureCoinBalance] = useState(0);
  const [myBalanceLoading, setMyBalanceLoading] = useState(true);

  // OTP Verification States
  const [isVerified, setIsVerified] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Partner Details (populated after OTP verification)
  const [partnerDetails, setPartnerDetails] = useState({
    name: "",
    number: "",
    partnerId: "",
    address: "",
  });

  // Timer countdown for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    localStorage.setItem("currentScreen", "cure_coin_screen");
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_screen_view",
    });
  }, []);

  useEffect(() => {
    const fetchMyCureCoinBalance = async () => {
      if (userData?.retailerCode) {
        setMyBalanceLoading(true);
        try {
          const response = await getRetailerLedgerBalance(
            userData.retailerCode,
          );
          const ledgerData = response?.data?.data?.ledgerView;
          setMyCureCoinBalance(ledgerData?.cureCoinAmount || 0);
        } catch (error) {
          setMyCureCoinBalance(0);
        } finally {
          setMyBalanceLoading(false);
        }
      } else {
        setMyBalanceLoading(false);
      }
    };
    fetchMyCureCoinBalance();
  }, [userData?.retailerCode]);

  // Debounced search for patients
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const response = await searchPatient(searchQuery);
          const patients = response?.data?.data || [];
          setSearchResults(patients);
          setShowDropdown((prev) =>
            selectedPatient ? false : patients.length > 0,
          );
        } catch (error) {
          setSearchResults([]);
          setShowDropdown(false);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedPatient]);

  // Format timer display (MM:SS)
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle OTP input change
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    setErrorMessage("");
  };

  // Handle Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_otp_resend",
    });

    try {
      await sendMobileOtp({ mobileNumber });
      setTimer(120);
      setOtp("");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error?.error ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setErrorMessage("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_otp_verify",
    });

    try {
      const response = await validateOtp({ mobileNumber, otp });
      if (response?.data?.status === "OK") {
        // Fetch coin balance using patient code
        const patientId = selectedPatient?.code || selectedPatient?.patientId;
        try {
          const pointsResponse = await getAllPoints(patientId);
          const pointsData = pointsResponse?.data?.data?.[0];
          setCoinBalance(pointsData?.medWalletPoints || 0);
        } catch (err) {
          setCoinBalance(0);
        }

        setIsVerified(true);
        setPartnerDetails({
          name: `${selectedPatient?.firstName || ""} ${selectedPatient?.lastName || ""}`.trim(),
          number: selectedPatient?.mobile || mobileNumber,
          partnerId: patientId || "",
          address: selectedPatient?.address || "",
        });
      } else {
        setErrorMessage("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error?.error ||
          "Invalid OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle key press for Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (!otpSent && selectedPatient) {
        handleCheckBalance();
      } else if (otpSent) {
        handleVerifyOtp();
      }
    }
  };

  const handleRedeem = () => {
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_redeem_click",
    });
    setShowRedeemModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setSelectedPatient(null);
    setOtpSent(false);
    setOtp("");
    setErrorMessage("");
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchQuery(
      `${patient.firstName || ""} ${patient.lastName || ""}`.trim(),
    );
    setMobileNumber(patient.mobile || "");
    setShowDropdown(false);
    setOtpSent(false);
    setOtp("");
    setErrorMessage("");
  };

  const handleCheckBalance = async () => {
    if (!selectedPatient?.mobile) {
      setErrorMessage("No mobile number available for this patient");
      return;
    }

    setLoading(true);
    Common_Event_Hit("DYNAMIC_EVENT", {
      eventName: "cure_coin_check_balance_otp_send",
      mobileNumber: selectedPatient.mobile,
    });

    try {
      await sendMobileOtp({ mobileNumber: selectedPatient.mobile });
      setOtpSent(true);
      setTimer(120);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.error?.error ||
          "Failed to send OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <ErrorModal
        show={errorModal.show}
        content={errorModal.content}
        apiData={errorModal?.apiData}
        onHide={() => {
          setErrorModal({ show: false, content: "" });
        }}
      />

      <RedeemCoinModal
        show={showRedeemModal}
        onHide={() => setShowRedeemModal(false)}
        coinBalance={coinBalance}
        verifiedMobileNumber={mobileNumber}
        patientId={partnerDetails.partnerId}
        setCoinBalance={setCoinBalance}
      />

      <Container fluid className="productList">
        <Container fluid className="productListHeader font_ubuntu">
          <HiOutlineArrowNarrowLeft
            className="cursor_pointer goBack"
            onClick={() => navigate(-1)}
          />
          &nbsp;&nbsp;&nbsp; Cure Coin
        </Container>

        <Container className="mt-4">
          {/* My Cure Coin Balance - Show only before verification */}
          {!isVerified && (
            <Card
              className="mb-4 shadow-sm"
              style={{
                borderRadius: "16px",
                background: "linear-gradient(135deg, #18406E 0%, #2E5C8F 100%)",
              }}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-1"
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "12px",
                      }}>
                      Retailer Code
                    </p>
                    <h5
                      className="mb-0 font_ubuntu font_weight_md"
                      style={{ color: "#fff" }}>
                      {userData?.retailerCode || "-"}
                    </h5>
                  </div>
                  <div className="text-end">
                    <p
                      className="mb-1"
                      style={{
                        color: "rgba(255,255,255,0.7)",
                        fontSize: "12px",
                      }}>
                      My Cure Coin Balance
                    </p>
                    <div className="d-flex align-items-center justify-content-end">
                      <PiCoinBold
                        style={{
                          fontSize: "24px",
                          color: "#FFD700",
                          marginRight: "8px",
                        }}
                      />
                      <h4
                        className="mb-0 font_weight_md"
                        style={{ color: "#FFD700" }}>
                        {myBalanceLoading ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ color: "#FFD700" }}
                          />
                        ) : (
                          myCureCoinBalance
                        )}
                      </h4>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {!isVerified && (
            <Card className="shadow-sm" style={{ borderRadius: "16px" }}>
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                      boxShadow: "0 4px 15px rgba(255, 215, 0, 0.3)",
                    }}>
                    <PiCoinBold style={{ fontSize: "40px", color: "#fff" }} />
                  </div>
                  <h4 className="color_primary font_ubuntu font_weight_md mb-2">
                    Cure Coin
                  </h4>
                  <p className="color_grey2 font_14 mb-0">
                    Search patient to check Cure Coin balance
                  </p>
                </div>

                {/* Error Alert */}
                {errorMessage && (
                  <Alert variant="danger" className="font_14 mb-3">
                    {errorMessage}
                  </Alert>
                )}

                <Form.Group className="mb-3" style={{ position: "relative" }}>
                  <Form.Label className="font_ubuntu font_14 font_weight_md color_primary">
                    Search Patient
                  </Form.Label>
                  <InputGroup
                    style={{ borderRadius: "10px", overflow: "hidden" }}>
                    <InputGroup.Text
                      style={{
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRight: "none",
                      }}>
                      <BsSearch className="color_primary" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by patient name or mobile number"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      disabled={otpSent || loading}
                      className="font_ubuntu"
                      style={{
                        border: "1px solid #dee2e6",
                        borderLeft: "none",
                        padding: "12px 16px",
                        fontSize: "16px",
                      }}
                    />
                    {searchLoading && (
                      <InputGroup.Text
                        style={{
                          background: "#fff",
                          border: "1px solid #dee2e6",
                          borderLeft: "none",
                        }}>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"></span>
                      </InputGroup.Text>
                    )}
                  </InputGroup>

                  {showDropdown && searchResults.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid #dee2e6",
                        borderRadius: "10px",
                        marginTop: "4px",
                        maxHeight: "250px",
                        overflowY: "auto",
                        zIndex: 1000,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}>
                      {searchResults.map((patient, index) => (
                        <div
                          key={patient.id || index}
                          onClick={() => handleSelectPatient(patient)}
                          style={{
                            padding: "12px 16px",
                            cursor: "pointer",
                            borderBottom:
                              index < searchResults.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.background = "#f8f9fa")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.background = "#fff")
                          }>
                          <div className="font_weight_md color_primary font_14">
                            {patient.salutation} {patient.firstName}{" "}
                            {patient.lastName}
                          </div>
                          <div className="color_grey2 font_12">
                            {patient.mobile} | {patient.email}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>

                <div
                  className="d-flex align-items-start mb-3"
                  style={{
                    background: "#EEF1FC",
                    borderRadius: "10px",
                    padding: "14px 16px",
                  }}>
                  <BsInfoCircleFill
                    style={{
                      color: "#4C6FE0",
                      fontSize: "18px",
                      marginRight: "10px",
                      marginTop: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <p
                    className="mb-0 font_14 color_primary"
                    style={{ lineHeight: "1.5" }}>
                    <strong>Important:</strong> Kavach Coins are credited at{" "}
                    <strong>1.5&times; the eligible value.</strong> Kavach Coins
                    can be redeemed only on <strong>Generic products at MRP</strong>{" "}
                    and are <strong>not applicable on PTR</strong>.
                  </p>
                </div>

                {selectedPatient && !otpSent && (
                  <Card
                    className="mb-3"
                    style={{ borderRadius: "10px", background: "#f8f9fa" }}>
                    <Card.Body className="p-3">
                      <h6 className="color_primary font_ubuntu font_weight_md mb-3">
                        Patient Details
                      </h6>
                      <div className="row">
                        <div className="col-6 mb-2">
                          <span className="color_grey2 font_12">
                            Patient ID
                          </span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            {selectedPatient.code}
                          </p>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="color_grey2 font_12">Name</span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            {selectedPatient.salutation}{" "}
                            {selectedPatient.firstName}{" "}
                            {selectedPatient.lastName}
                          </p>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="color_grey2 font_12">Mobile</span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            +91 {selectedPatient.mobile}
                          </p>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="color_grey2 font_12">Email</span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            {selectedPatient.email || "-"}
                          </p>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="color_grey2 font_12">Gender</span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            {selectedPatient.gender === "M"
                              ? "Male"
                              : selectedPatient.gender === "F"
                                ? "Female"
                                : "-"}
                          </p>
                        </div>
                        <div className="col-6 mb-2">
                          <span className="color_grey2 font_12">
                            Date of Birth
                          </span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            {selectedPatient.dob || "-"}
                          </p>
                        </div>
                        <div className="col-6">
                          <span className="color_grey2 font_12">Age</span>
                          <p className="color_primary font_weight_md mb-0 font_14">
                            {selectedPatient.age || "-"} years
                          </p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* OTP Input - Show after OTP sent */}
                {otpSent && (
                  <>
                    <div
                      className="mb-3 p-3"
                      style={{ background: "#e8f5e9", borderRadius: "10px" }}>
                      <p className="mb-0 font_14 color_primary">
                        OTP sent to <strong>+91 {mobileNumber}</strong>
                      </p>
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label className="font_ubuntu font_14 font_weight_md color_primary">
                        Enter OTP
                      </Form.Label>
                      <InputGroup
                        style={{ borderRadius: "10px", overflow: "hidden" }}>
                        <InputGroup.Text
                          style={{
                            background: "#f8f9fa",
                            border: "1px solid #dee2e6",
                            borderRight: "none",
                          }}>
                          <BsShieldCheck className="color_secondary" />
                        </InputGroup.Text>
                        <Form.Control
                          type="tel"
                          placeholder="Enter 6-digit OTP"
                          value={otp}
                          onChange={handleOtpChange}
                          onKeyPress={handleKeyPress}
                          disabled={loading}
                          className="font_ubuntu"
                          maxLength={6}
                          autoFocus
                          style={{
                            border: "1px solid #dee2e6",
                            borderLeft: "none",
                            padding: "12px 16px",
                            fontSize: "16px",
                            letterSpacing: "8px",
                          }}
                        />
                      </InputGroup>
                    </Form.Group>

                    {/* Timer and Resend */}
                    <div className="text-center mb-3">
                      {timer > 0 ? (
                        <span className="color_grey font_14">
                          Resend OTP in{" "}
                          <span className="color_secondary font_weight_md">
                            {formatTimer(timer)}
                          </span>
                        </span>
                      ) : (
                        <Button
                          variant="link"
                          className="p-0 font_14 color_secondary text-decoration-none"
                          onClick={handleResendOtp}
                          disabled={loading}>
                          Didn't receive OTP? <strong>Resend</strong>
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {/* Action Button */}
                {!otpSent ? (
                  <Button
                    className="w-100 bg_secondary brdr_none font_ubuntu color_white"
                    style={{
                      padding: "14px",
                      borderRadius: "10px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={handleCheckBalance}
                    disabled={!selectedPatient || loading}>
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"></span>
                        Sending OTP...
                      </>
                    ) : (
                      "Check Available Cure Coin Balance"
                    )}
                  </Button>
                ) : (
                  <Button
                    className="w-100 bg_secondary brdr_none font_ubuntu color_white"
                    style={{
                      padding: "14px",
                      borderRadius: "10px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={handleVerifyOtp}
                    disabled={otp.length !== 6 || loading}>
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"></span>
                        Verifying...
                      </>
                    ) : (
                      "Verify & Continue"
                    )}
                  </Button>
                )}
              </Card.Body>
            </Card>
          )}

          {isVerified && (
            <>
              <Card className="mb-4 shadow-sm" style={{ borderRadius: "16px" }}>
                <Card.Body className="p-4">
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #18406E 0%, #2E5C8F 100%)",
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "20px",
                    }}>
                    <div className="text-center">
                      <PiCoinBold
                        style={{
                          fontSize: "60px",
                          color: "#FFD700",
                          marginBottom: "10px",
                        }}
                      />
                      <h4 className="color_white font_ubuntu mb-1">
                        Your Coin Balance
                      </h4>
                      <h1
                        style={{
                          fontSize: "48px",
                          fontWeight: "bold",
                          color: "#FFD700",
                          margin: "0",
                        }}>
                        {coinBalance}
                      </h1>
                      <p
                        className="color_white mt-2 mb-0"
                        style={{ opacity: 0.8 }}>
                        Cure Coins can be redeemed for discounts
                      </p>
                    </div>
                  </div>

                  <h5 className="color_primary font_ubuntu mb-3">
                    Partner Details
                  </h5>
                  <div
                    className="bg_light p-3"
                    style={{ borderRadius: "10px" }}>
                    <div className="mb-3">
                      <span className="color_grey2 font_12">Partner Name</span>
                      <p className="color_primary font_weight_md mb-0 font_16">
                        {partnerDetails.name}
                      </p>
                    </div>
                    <div className="mb-3">
                      <span className="color_grey2 font_12">
                        Partner Number
                      </span>
                      <p className="color_primary font_weight_md mb-0 font_16">
                        +91 {partnerDetails.number}
                      </p>
                    </div>
                    <div className="mb-3">
                      <span className="color_grey2 font_12">Partner ID</span>
                      <p className="color_primary font_weight_md mb-0 font_16">
                        {partnerDetails.partnerId}
                      </p>
                    </div>
                    <div>
                      <span className="color_grey2 font_12">Address</span>
                      <p className="color_primary font_weight_md mb-0 font_14">
                        {partnerDetails.address}
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="color_primary font_ubuntu mb-3">
                    Redeem Your Coins
                  </h5>
                  <p className="color_grey2 mb-4">
                    Use your Cure Coins to get amazing discounts on your next
                    order!
                  </p>
                  <div
                    className="bg_light p-3 mb-3"
                    style={{ borderRadius: "8px" }}>
                    <div className="display_flex space_btw mb-2">
                      <span className="font_weight_md">100 Coins</span>
                      <span className="color_secondary">= ₹100 OFF</span>
                    </div>
                    <div className="display_flex space_btw mb-2">
                      <span className="font_weight_md">500 Coins</span>
                      <span className="color_secondary">= ₹500 OFF</span>
                    </div>
                    <div className="display_flex space_btw">
                      <span className="font_weight_md">1000 Coins</span>
                      <span className="color_secondary">= ₹1000 OFF</span>
                    </div>
                  </div>
                  <Button
                    className="w-100 bg_secondary brdr_none font_ubuntu color_white"
                    style={{ padding: "12px", borderRadius: "10px" }}
                    onClick={handleRedeem}
                    disabled={coinBalance < 20}>
                    {coinBalance < 20
                      ? "Minimum 20 Coins Required"
                      : "Redeem Now"}
                  </Button>
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </Container>
    </Fragment>
  );
};

export default CureCoin;
